// The Filter widget on ?live=1: Ben's own matching (`data/filter.ts` computeFilterMatches) run on
// the shared group (`live/group-players.ts`) — her league's file for the season picked.
//
// As Ranking (`live/ranking.ts`): with a Position chip on, the rows are minutes AT each position
// (Ben's Position rule, rules.md), so a player who matches at two of the chip's positions is listed
// once per position (Q-014). Each such row says which position it is ("at Full Back") where the
// snapshot shows the league — the readout already names the league.

import type { ProtoFilterResult, ProtoPlayerMetric } from "@catbento/shared";
import { computeFilterMatches, type FilterMetricRanges } from "../data/filter.js";
import { comparePlayersByName } from "../data/player-utils.js";
import { humanizePositionTemplate } from "../modules/bio-view.js";
import { LIVE_FILTER_RANGE_FILTERS } from "../modules/filter-view.js";
import type { ActiveFilters, KitPlayer } from "../engines/stats-filter-kit.js";
import type { GroupPlayer } from "./group-players.js";

/** The fixed columns every file row has (`groupRowsOf` adds them itself), so not asked for by name. */
const FIXED = new Set(["minutes", "nineties_played"]);

/**
 * The metric keys to read from the file: every slider's (the whole catalog — the Filter shows them
 * all). A key the file lacks still throws in `groupPlayers` (the engine-mismatch gotcha); the
 * catalog's 162 snapshot keys hold none of v1.7.0's renamed pass-% keys, and the hidden
 * `claim_success_perc` reads as no value (its slider: "No data"), so old and new files both load.
 */
export function filterMetricKeys(metricMap: ReadonlyMap<string, ProtoPlayerMetric>): string[] {
  return [...metricMap.keys()].filter((k) => !FIXED.has(k));
}

export function liveFilterMatches(
  players: Record<string, GroupPlayer>,
  activeBio: ActiveFilters,
  refPlayer: KitPlayer,
  seasonCode: string,
  metricRanges: FilterMetricRanges,
  mode: string,
  metricMap: Map<string, ProtoPlayerMetric>,
  byPosition: boolean,
): ProtoFilterResult[] {
  const match = (ps: Record<string, GroupPlayer>) =>
    computeFilterMatches(ps, activeBio, refPlayer, seasonCode, metricRanges, mode, metricMap, LIVE_FILTER_RANGE_FILTERS);
  if (!byPosition) return match(players);
  // Matched per position (each player at most once in each), labelled, then merged back into
  // Ben's by-name order. The sort is stable, so her rows keep the file's position order.
  const byGroup = new Map<string, Record<string, GroupPlayer>>();
  for (const [key, p] of Object.entries(players)) {
    const group = p.positionTemplate ?? "";
    let ps = byGroup.get(group);
    if (!ps) byGroup.set(group, (ps = {}));
    ps[key] = p;
  }
  return [...byGroup]
    .flatMap(([group, ps]) => {
      // Her minutes at this position, for the "why twice" hover (group-players `whyTwice`).
      const minutes = new Map(Object.values(ps).map((p) => [p.playerId, p.stats.minutes ?? null] as const));
      return match(ps).map((e) => ({
        ...e,
        competition: `at ${humanizePositionTemplate(group)}`,
        positionMinutes: minutes.get(e.playerId) ?? null,
      }));
    })
    .sort(comparePlayersByName);
}
